import { useState } from 'react';
import ExcelJS from 'exceljs';
import { FaFileUpload, FaTimes } from 'react-icons/fa';
import ModalShell from './ModalShell';

const parseDate = (dateVal) => {
  if (!dateVal) return null;

  // Si ExcelJS retorna un objeto Date
  if (dateVal instanceof Date) {
    // Asegurarnos que tome día/mes/año correctamente. 
    // Nota: ExcelJS suele entregar fechas con hora 00:00 en zona horaria local o UTC según cómo se lea,
    // pero para propósito de fecha pura, usamos getDate/getMonth
    const d = String(dateVal.getDate()).padStart(2, '0');
    const m = String(dateVal.getMonth() + 1).padStart(2, '0');
    const y = dateVal.getFullYear();
    return `${d}-${m}-${y}`;
  }

  // Si es número (serial date), aunque ExcelJS suele convertirlo a Date
  if (typeof dateVal === 'number') {
    // Conversión de fecha serial Excel (base 1900)
    // - 25569 es la diferencia de días entre 1/1/1970 y 1/1/1900
    // * 86400 * 1000 para pasar a milisegundos
    // Ajuste simple. ExcelJS debería encargarse, pero por seguridad:
    const date = new Date(Math.round((dateVal - 25569) * 86400 * 1000));
    const d = String(date.getUTCDate()).padStart(2, '0');
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const y = date.getUTCFullYear();
    return `${d}-${m}-${y}`;
  }

  const str = String(dateVal).trim();

  // Formato DD/MM/YYYY o DD-MM-YYYY
  const parts = str.split(/[-/]/);

  if (parts.length === 3) {
    let [d, m, y] = parts;

    // Si el año está en posición 0, es formato YYYY-MM-DD
    if (parts[0].length === 4) {
      [y, m, d] = parts;
    }

    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }

  return null;
};

const parseTime = (timeVal) => {
  if (!timeVal && timeVal !== 0) return '';

  // Si ExcelJS retorna Date (para celdas de tiempo, a veces retorna fecha base con hora)
  if (timeVal instanceof Date) {
    const h = String(timeVal.getHours()).padStart(2, '0');
    const m = String(timeVal.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  // Si Excel envía un decimal (fracción del día)
  if (typeof timeVal === 'number') {
    const totalMinutes = Math.round(timeVal * 24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  const str = String(timeVal).trim();

  // Buscar patrón HH:MM
  const match = str.match(/(\d{1,2}):(\d{2})/);

  if (match) {
    const [_, h, m] = match;
    return `${h.padStart(2, '0')}:${m}`;
  }

  return str;
};

const convertToISO = (ddmmyyyy) => {
  if (!ddmmyyyy) return null;
  const [d, m, y] = ddmmyyyy.split('-');
  return `${y}-${m}-${d}`;
};

// Calcular horas desde inicio y fin
const calculateHours = (start, end) => {
  if (!start || !end) return 0;

  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);

  if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM)) return 0;

  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  if (endMinutes <= startMinutes) return 0;

  return Math.round(((endMinutes - startMinutes) / 60) * 100) / 100;
};

export default function ImportWorklogsExcel({
  projectId,
  memberId,
  onImport,
  render
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const buffer = evt.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        // Obtener primera hoja
        const sheet = workbook.getWorksheet(1);
        if (!sheet) {
          throw new Error("El archivo Excel no tiene hojas");
        }

        // Convertir filas a array (para simular estructura anterior)
        const rows = [];
        sheet.eachRow({ includeEmpty: false }, (row) => {
          // row.values tiene los valores comenzando desde índice 1
          // Convertimos a array normal 0-based
          const rowValues = [];
          // Es mejor iterar solo las celdas que necesitamos o mapear todo
          // row.cellCount da el número de celdas utilizadas
          // row.values es [empty, val1, val2, ...]

          const valArray = row.values;
          // ExcelJS row.values es [ <empty>, col1, col2, ... ]
          if (Array.isArray(valArray)) {
            // Copiar desde índice 1
            for (let k = 1; k < valArray.length; k++) {
              rowValues[k - 1] = valArray[k];
            }
          } else if (typeof valArray === 'object') {
            // Si es un objeto (sparse)
            Object.keys(valArray).forEach(k => {
              const idx = parseInt(k, 10);
              rowValues[idx - 1] = valArray[k];
            });
          }

          // Llenar huecos con string vacío para evitar undefined
          // Asumiendo que leemos al menos 4 columnas
          for (let i = 0; i < 4; i++) {
            if (rowValues[i] === undefined || rowValues[i] === null) {
              // Verificar si la celda tiene un objeto (ej: Hyperlink/RichText)
              const cell = row.getCell(i + 1);
              if (cell && (cell.value === null || cell.value === undefined)) {
                rowValues[i] = '';
              } else {
                // Si es objeto complejo tomar .text o .result
                const v = cell.value;
                if (v && typeof v === 'object') {
                  rowValues[i] = v.text || v.result || '';
                } else {
                  rowValues[i] = v;
                }
              }
            }
          }

          rows.push(rowValues);
        });

        const parsed = [];
        const errs = [];

        // Detectar fila de encabezado (buscar palabras clave en primera fila leída)
        // Adjust for rows possibly having headers
        let startIdx = 0;
        if (rows.length > 0 && /día|fecha|date/i.test(String(rows[0][0]))) {
          startIdx = 1;
        }

        for (let i = startIdx; i < rows.length; i++) {
          const row = rows[i];
          // Número de fila real aproximado (relativo a datos leídos)
          // ExcelJS sheet.eachRow nos da el rowNumber real, pero aquí lo aplanamos.
          // Simplement usamos i + 1 + (headers?1:0).
          // Para ser precisos, hubiera sido mejor usar el callback, pero mantenemos lógica simple.
          const rowNum = i + 1; // Referencial

          // Saltar filas vacías
          if (!row || row.length === 0 || row.every(cell => !cell && cell !== 0)) continue;

          // IMPORTANTE: Asegurarse de que haya al menos 4 columnas
          const [dateCell, startCell, endCell, noteCell] = row;

          const date = parseDate(dateCell);
          const start = parseTime(startCell);
          const end = parseTime(endCell);

          // Nota: manejar si noteCell es objeto RichText
          let note = '';
          if (noteCell && typeof noteCell === 'object' && noteCell.richText) {
            note = noteCell.richText.map(t => t.text).join('');
          } else if (noteCell && typeof noteCell === 'object' && noteCell.text) {
            note = noteCell.text;
          } else {
            note = String(noteCell || '');
          }
          note = note.trim();

          // Validaciones
          if (!date) {
            errs.push(`Fila ${rowNum}: Fecha inválida - "${dateCell instanceof Date ? dateCell.toLocaleDateString() : dateCell}"`);
            continue;
          }

          if (!start || !end) {
            errs.push(`Fila ${rowNum}: Horas inválidas - Inicio: "${startCell}", Fin: "${endCell}"`);
            continue;
          }

          if (!note) {
            errs.push(`Fila ${rowNum}: Comentario vacío`);
            continue;
          }

          const hours = calculateHours(start, end);

          parsed.push({
            date,
            dateISO: convertToISO(date),
            start,
            end,
            note,
            hours,
            rowNum
          });
        }

        setRecords(parsed);
        setErrors(errs);
        setModalOpen(true);

      } catch (error) {
        console.error(error);
        alert(`Error al leer el archivo: ${error.message}`);
      }
    };

    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!records.length) return;

    try {
      await onImport(records);
      setModalOpen(false);
      setRecords([]);
      setErrors([]);
      setFileName('');
    } catch (error) {
      alert(`Error al importar: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
    setRecords([]);
    setErrors([]);
    setFileName('');
  };

  // Calcular total de horas
  const totalHours = records.reduce((sum, rec) => sum + (rec.hours || 0), 0);

  return (
    <>
      {render ? (
        render(() => document.getElementById('import-excel-input')?.click())
      ) : (
        <button
          className="btn"
          onClick={() => document.getElementById('import-excel-input')?.click()}
          title="Importar registros desde Excel"
        >
          <span className="btn-with-icon">
            <FaFileUpload /> Importar Excel
          </span>
        </button>
      )}

      <input
        id="import-excel-input"
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      <ModalShell
        open={modalOpen}
        onClose={handleCancel}
        title={`Importar registros desde ${fileName}`}
        width={1200}
        maxWidth="98vw"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {errors.length > 0 && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: 12,
              color: '#dc2626'
            }}>
              <strong>⚠️ Advertencias ({errors.length}):</strong>
              <ul style={{ margin: '8px 0 0 20px', fontSize: 14 }}>
                {errors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {errors.length > 5 && <li>... y {errors.length - 5} más</li>}
              </ul>
            </div>
          )}

          <div style={{
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 8,
            padding: 12,
            color: '#15803d',
            fontWeight: 500
          }}>
            ✓ Se importarán {records.length} registro(s) válido(s) • <strong>{totalHours.toFixed(2)} horas totales</strong>
          </div>

          {records.length > 0 && (
            <div style={{
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              overflow: 'hidden',
              maxHeight: 500,
              overflowY: 'auto'
            }}>
              <table className="worklog-table" style={{ marginBottom: 0 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th style={{ width: 120 }}>Fecha</th>
                    <th style={{ width: 80 }}>Inicio</th>
                    <th style={{ width: 80 }}>Fin</th>
                    <th style={{ width: 80, textAlign: 'right' }}>Horas</th>
                    <th>Comentario</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec, idx) => (
                    <tr key={idx}>
                      <td style={{ textAlign: 'center', color: '#64748b' }}>{rec.rowNum}</td>
                      <td style={{ fontFamily: 'monospace' }}>{rec.date}</td>
                      <td style={{ fontFamily: 'monospace' }}>{rec.start}</td>
                      <td style={{ fontFamily: 'monospace' }}>{rec.end}</td>
                      <td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>
                        {rec.hours.toFixed(2)}
                      </td>
                      <td style={{
                        maxWidth: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {rec.note}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600, padding: 12 }}>
                      Total:
                    </td>
                    <td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 700, fontSize: 16, color: '#15803d', padding: 12 }}>
                      {totalHours.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 8,
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              className="btn secondary"
              onClick={handleCancel}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FaTimes /> Cancelar
            </button>

            <button
              className="btn"
              onClick={handleConfirmImport}
              disabled={records.length === 0}
              style={{
                background: records.length === 0 ? '#e5e7eb' : undefined,
                cursor: records.length === 0 ? 'not-allowed' : undefined
              }}
            >
              Importar {records.length} registro(s) ({totalHours.toFixed(2)} hrs)
            </button>
          </div>
        </div>
      </ModalShell>
    </>
  );
}